import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { Alert } from '../alert';
import { Avatar } from '../avatar';
import { FlexBox } from '../box';
import { Breadcrumbs } from '../breadcrumbs';
import { Button, IconButton } from '../button';
import { ButtonGroupSplit } from '../button-group';
import { Chip } from '../chip';
import { ColorIndicator } from '../color-indicator';
import { FacilityBadge } from '../facility-picker';
import { getIcon } from '../icon';
import { Menu } from '../menu';
import { Skeleton } from '../skeleton';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
export function sortPageHeaderActions(actions) {
    const pageActions = actions.filter(isPageAction);
    return [
        ...pageActions.filter((action) => action.type === 'outlined'),
        ...pageActions.filter((action) => action.type === 'menu'),
        ...actions.filter(isPageActionGroup),
        ...pageActions.filter((action) => action.type === 'contained')
    ];
}
export const ActionButtons = (props) => {
    const sortedActions = sortPageHeaderActions(props.actions);
    const actions = sortedActions.filter(isPageAction);
    const actionGroups = sortedActions.filter(isPageActionGroup);
    const containedActions = actions.filter((action) => action.type === 'contained');
    const outlinedActions = actions.filter((action) => action.type === 'outlined');
    const menuActions = actions.filter((action) => action.type === 'menu');
    return (_jsxs(FlexBox, { gap: 1, vAlign: "center", children: [outlinedActions.map((action, i) => (_jsx(ActionButton, { action: action, variant: "outlined" }, typeof action.label === 'string' ? action.label : i))), actionGroups.map((group, index) => (_jsx(ActionButtonGroup, { actionGroup: group }, index))), containedActions.map((action, i) => (_jsx(ActionButton, { action: action, variant: "contained" }, typeof action.label === 'string' ? action.label : i))), props.facilityPicker, menuActions.length > 0 && (_jsx(Menu, { actions: menuActions.map((action) => ({
                    primary: action.label,
                    group: action.group,
                    disabled: action.disabled,
                    onClick: action.onClick
                })) }))] }));
};
const ActionButtonGroup = (props) => {
    return (_jsx(ButtonGroupSplit, { actions: props.actionGroup.map((action) => ({
            primary: action.label,
            disabled: action.disabled,
            onClick: action.onClick
        })), variant: "contained" }));
};
const ActionButton = (props) => {
    const { action, variant } = props;
    const Icon = action.icon ? getIcon(action.icon) : undefined;
    const button = (_jsx(Button, { variant: variant, color: "primary", disabled: action.disabled, onClick: action.onClick, "data-role": action['data-role'], startIcon: Icon ? _jsx(Icon, {}) : undefined, children: action.label }));
    if (action.tooltip) {
        return _jsx(Tooltip, { title: action.tooltip, children: button });
    }
    return button;
};
const HeaderContent = (props) => {
    const { breadcrumbs, title, secondaryText, colorIndicator, facilityBadge, icon, iconButton, actions, facilityPicker, description, chips, alert } = props;
    const hasTitle = title || secondaryText || colorIndicator || icon || iconButton;
    const Icon = icon ? getIcon(icon) : undefined;
    return (_jsxs(_Fragment, { children: [breadcrumbs && breadcrumbs.length > 0 && (_jsx(Breadcrumbs, { items: breadcrumbs })), _jsxs(FlexBox, { hAlign: "space-between", vAlign: "flex-start", gap: 3, children: [_jsxs(FlexBox, { stack: true, gap: 0.5, autoShrink: true, children: [hasTitle && (_jsxs(FlexBox, { gap: 1, vAlign: "center", children: [colorIndicator && (_jsx(ColorIndicator, { color: colorIndicator })), title && (_jsx(Typography, { variant: "h5", color: "text.primary", fontWeight: "bold", noWrap: true, children: title })), secondaryText && (_jsx(Typography, { variant: "h5", color: "text.secondary", fontWeight: "bold", noWrap: true, children: secondaryText })), facilityBadge && (_jsx(FacilityBadge, { ...facilityBadge })), Icon && (_jsx(Icon, { fontSize: "medium", color: "action.active" })), iconButton && _jsx(IconButton, { ...iconButton })] })), chips?.length !== 0 && (_jsx(FlexBox, { gap: 1, vAlign: "center", children: chips?.map((chipProps, index) => (_jsx(Chip, { ...chipProps }, index))) }))] }), (actions?.length || facilityPicker) && (_jsx(FlexBox, { gap: 1, vAlign: "center", hAlign: "flex-end", children: actions?.length ? (_jsx(ActionButtons, { actions: actions, facilityPicker: facilityPicker })) : (facilityPicker) }))] }), description && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: description })), alert?.children && _jsx(Alert, { ...alert })] }));
};
export const Header = React.forwardRef((props, ref) => {
    const { avatar, loading, className, 'data-role': dataRole = 'page-header', 'data-role-value': dataRoleValue, ...contentProps } = props;
    const baseProps = {
        className,
        'data-role': dataRole,
        'data-role-value': dataRoleValue,
        bgcolor: 'background.paper',
        py: 2,
        zIndex: 1,
        position: 'sticky',
        top: 0,
        sx: { '+ *': { mt: -2 } }
    };
    if (loading) {
        return (_jsxs(FlexBox, { ...baseProps, ref: ref, stack: true, gap: 1, children: [_jsx(Skeleton, { width: 200, height: 20 }), _jsx(Skeleton, { width: "25vw", height: 32 })] }));
    }
    const hasTitle = contentProps.title ||
        contentProps.secondaryText ||
        contentProps.colorIndicator ||
        contentProps.icon ||
        contentProps.iconButton;
    const hasContent = hasTitle ||
        contentProps.breadcrumbs?.length ||
        contentProps.actions?.length;
    if (!hasContent && !avatar) {
        return null;
    }
    if (avatar && contentProps.colorIndicator) {
        console.warn('[Page.Header] "avatar" and "colorIndicator" should not be used together. The avatar layout will be used.');
    }
    if (avatar) {
        return (_jsxs(FlexBox, { ...baseProps, ref: ref, gap: 2, children: [_jsx(Avatar, { ...avatar }), _jsx(FlexBox, { stack: true, gap: 1, autoShrink: true, children: _jsx(HeaderContent, { ...contentProps }) })] }));
    }
    return (_jsx(FlexBox, { ...baseProps, ref: ref, stack: true, gap: 1, children: _jsx(HeaderContent, { ...contentProps }) }));
});
Header.displayName = 'Header';
export function isPageAction(action) {
    return Boolean(action && 'label' in action);
}
export function isPageActionGroup(action) {
    return Array.isArray(action);
}
